import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, MessagesSquare, MapPin, Calendar, BadgeCheck, User, Lock, Coins, Loader2 } from 'lucide-react';
import {
  useAppSelector,
  selectIsAuthenticated,
  selectCurrentUser,
  useCheckContactUnlockQuery,
  useUnlockContactMutation,
  useGetCreditConfigQuery,
} from '@nimbazaar/store';
import { AuthModal } from './AuthModal';
import { useToast } from './Toast';

interface SellerInfo {
  id: string;
  name: string;
  avatar?: string;
  phone?: string;
  emailVerified: boolean;
  createdAt: Date | string;
  profile?: {
    bio?: string;
    location?: string;
  };
}

interface SellerProfileCardProps {
  seller: SellerInfo;
  adTitle: string;
  adId?: string;
}

export function SellerProfileCard({ seller, adTitle, adId }: SellerProfileCardProps) {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const currentUser = useAppSelector(selectCurrentUser);
  const toast = useToast();

  const isOwnAd = currentUser?.id === seller.id;

  // Check if contact is already unlocked
  const { data: unlockData, refetch: refetchUnlock } = useCheckContactUnlockQuery(adId || '', {
    skip: !adId || !isAuthenticated || isOwnAd,
  });
  const [unlockContact, { isLoading: isUnlocking }] = useUnlockContactMutation();
  const { data: creditConfigData } = useGetCreditConfigQuery();
  const unlockCost = creditConfigData?.data?.AD_REWARD ?? 5;

  const isUnlocked = isOwnAd || (unlockData?.data?.unlocked ?? false);

  const memberSince = new Date(seller.createdAt);
  const memberSinceText = memberSince.toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  });

  const handleUnlock = async () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    if (!adId) return;

    try {
      const res = await unlockContact(adId).unwrap();
      if (res.message) toast.success('Contact Unlocked', res.message);
      refetchUnlock();
    } catch (err: unknown) {
      const error = err as { status?: number; data?: { error?: { message?: string } } };
      if (error?.status === 402) {
        toast.error('Insufficient Credits', `You need ${unlockCost} credits to view contact info.`);
      } else {
        toast.error('Error', error?.data?.error?.message || 'Failed to unlock contact.');
      }
    }
  };

  const getWhatsAppLink = () => {
    if (!seller.phone) return '';
    let phone = seller.phone.replace(/[\s-]/g, '');
    if (phone.startsWith('0')) {
      phone = '92' + phone.slice(1);
    } else if (!phone.startsWith('+') && !phone.startsWith('92')) {
      phone = '92' + phone;
    }
    phone = phone.replace('+', '');
    const message = encodeURIComponent(`Hi, I'm interested in your ad: ${adTitle}`);
    return `https://wa.me/${phone}?text=${message}`;
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 lg:sticky lg:top-20">
      {/* Seller Info */}
      <div className="flex items-start gap-4 mb-4">
        <div className="flex-shrink-0">
          {seller.avatar ? (
            <img
              src={seller.avatar}
              alt={seller.name}
              className="w-14 h-14 rounded-full object-cover border-2 border-gray-100"
            />
          ) : (
            <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center">
              <User className="w-7 h-7 text-primary-500" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <h3 className="font-semibold text-gray-900 truncate">{seller.name}</h3>
            {seller.emailVerified && (
              <BadgeCheck className="w-5 h-5 text-primary-500 flex-shrink-0" />
            )}
          </div>
          {seller.profile?.location && (
            <div className="flex items-center gap-1 text-gray-500 text-sm mt-1">
              <MapPin className="w-4 h-4" />
              <span>{seller.profile.location}</span>
            </div>
          )}
          <div className="flex items-center gap-1 text-gray-500 text-sm mt-0.5">
            <Calendar className="w-4 h-4" />
            <span>Member since {memberSinceText}</span>
          </div>
        </div>
      </div>

      {seller.profile?.bio && (
        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
          {seller.profile.bio}
        </p>
      )}

      <div className="border-t border-gray-100 my-4" />

      {/* Locked state — show unlock button */}
      {!isUnlocked && !isOwnAd && (
        <div className="mb-4">
          <button
            onClick={handleUnlock}
            disabled={isUnlocking}
            className="w-full py-3 px-4 bg-gradient-to-r from-secondary-500 to-secondary-600 hover:from-secondary-600 hover:to-secondary-700 text-white font-medium rounded-lg transition-all flex items-center justify-center gap-2 shadow-sm disabled:opacity-60"
          >
            {isUnlocking ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Lock className="w-4 h-4" />
                Unlock Contact Info
                <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs flex items-center gap-1">
                  <Coins className="w-3 h-3" />
                  {unlockCost}
                </span>
              </>
            )}
          </button>
          <p className="text-xs text-gray-400 text-center mt-2">One-time charge per ad</p>
        </div>
      )}

      {/* Contact buttons — only when unlocked */}
      {isUnlocked && (
        <div className="space-y-3">
          {/* Show Phone */}
          <div className="w-full py-3 px-4 bg-gray-50 rounded-lg border border-gray-200">
            {seller.phone ? (
              <button
                onClick={() => {
                  navigator.clipboard.writeText(seller.phone!);
                  toast.success('Copied!', 'Phone number copied to clipboard.');
                }}
                className="flex items-center justify-center gap-2 text-gray-900 font-medium hover:text-primary-600 transition-colors w-full"
                title="Click to copy"
              >
                <Phone className="w-5 h-5" />
                <span>{seller.phone}</span>
              </button>
            ) : (
              <div className="flex items-center justify-center gap-2 text-gray-500">
                <Phone className="w-5 h-5" />
                <span>Phone not available</span>
              </div>
            )}
          </div>

          {/* WhatsApp */}
          {seller.phone ? (
            <a
              href={getWhatsAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-3 px-4 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" />
              Chat on WhatsApp
            </a>
          ) : (
            <button
              disabled
              className="w-full py-3 px-4 bg-gray-100 text-gray-400 font-medium rounded-lg cursor-not-allowed flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" />
              WhatsApp Not Available
            </button>
          )}

          {/* Chat on App — hide on own ad */}
          {adId && !isOwnAd && (
            <Link
              to={`/chat/${adId}/${seller.id}`}
              className="w-full py-3 px-4 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <MessagesSquare className="w-5 h-5" />
              Chat on App
            </Link>
          )}
        </div>
      )}

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  );
}
